import {
	Box,
	Flex,
	Text,
	Tooltip,
	useColorMode,
	IconButton,
	useOutsideClick,
	useBreakpointValue
} from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import {
	FiAward,
	FiBook,
	FiBookOpen,
	FiBriefcase,
	FiCode,
	FiCheck,
	FiHome,
	FiImage,
	FiMonitor,
	FiMoon,
	FiSettings,
	FiSun,
	FiTool,
	FiUser
} from 'react-icons/fi'
import { useLocation, useNavigate } from 'react-router-dom'
import backgroundContext from '../context/backgroundContext'
import accentContext from '../context/accentContext'
import loaderContext from '../context/loaderContext'
import recruiterModeContext from '../context/recruiterModeContext'
import { getTokens } from '../config/colorTokens'

const MotionBox = motion(Box)
const MotionFlex = motion(Flex)
const COLOR_MODE_KEY = 'site-color-mode-preference'

const defaultNavItems = [
	{ name: 'Home', icon: <FiHome />, path: '/' },
	{ name: 'About', icon: <FiUser />, path: '/about' },
	{ name: 'Education', icon: <FiBookOpen />, path: '/education' },
	{ name: 'Skills', icon: <FiTool />, path: '/skills' },
	{ name: 'Experience', icon: <FiBriefcase />, path: '/experience' },
	{ name: 'Projects', icon: <FiCode />, path: '/projects' },
	{ name: 'Achievements', icon: <FiAward />, path: '/achievements' },
	{ name: 'Publications', icon: <FiBook />, path: '/publications' }
]

const Navbar = () => {
	const { colorMode, setColorMode } = useColorMode()
	const context = useContext(backgroundContext)
	const { showBackground, setShowBackground } = context
	const { accentId, setAccentId, accentPalettes } = useContext(accentContext)
	const { recruiterMode } = useContext(recruiterModeContext)
	const loaderCtx = useContext(loaderContext)
	const { loading } = loaderCtx
	const navigate = useNavigate()
	const location = useLocation()
	const t = getTokens(colorMode)

	const isMobile = useBreakpointValue({ base: true, md: false })
	const settingsRef = useRef(null)
	const [settingsOpen, setSettingsOpen] = useState(false)
	const [colorModePreference, setColorModePreference] = useState(() => {
		if (typeof window === 'undefined') return 'system'
		return window.localStorage.getItem(COLOR_MODE_KEY) || 'system'
	})

	const setModeWithTransition = useCallback(
		(nextMode) => {
			if (typeof document === 'undefined' || !document.startViewTransition) {
				setColorMode(nextMode)
				return
			}

			document.startViewTransition(() => {
				flushSync(() => setColorMode(nextMode))
			})
		},
		[setColorMode]
	)

	const getSystemColorMode = () => {
		if (typeof window === 'undefined') return colorMode
		return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
	}

	const handleColorPreference = (preference) => {
		setColorModePreference(preference)
		window.localStorage.setItem(COLOR_MODE_KEY, preference)
		setModeWithTransition(preference === 'system' ? getSystemColorMode() : preference)
	}

	useEffect(() => {
		if (typeof window === 'undefined') return undefined

		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
		const syncSystemMode = () => {
			if (colorModePreference === 'system') {
				setModeWithTransition(mediaQuery.matches ? 'dark' : 'light')
			}
		}

		syncSystemMode()
		mediaQuery.addEventListener('change', syncSystemMode)

		return () => mediaQuery.removeEventListener('change', syncSystemMode)
	}, [colorModePreference, setModeWithTransition])

	useOutsideClick({
		ref: settingsRef,
		handler: () => setSettingsOpen(false)
	})

	useEffect(() => {
		const handleEscape = (event) => {
			if (event.key === 'Escape') setSettingsOpen(false)
		}

		window.addEventListener('keydown', handleEscape)
		return () => window.removeEventListener('keydown', handleEscape)
	}, [])

	if (loading) return null
	if (recruiterMode) return null

	// Variants for the navbar container
	const navbarVariants = {
		initial: { y: -18, opacity: 0, scale: 0.96 },
		animate: {
			y: 0,
			opacity: 1,
			scale: 1,
			transition: {
				type: 'spring',
				damping: 24,
				stiffness: 180,
				mass: 0.9,
				delay: 0.14
			}
		}
	}

	// Variants for individual items to stagger them if needed, or for hover
	const itemVariants = {
		initial: { scale: 1 },
		hover: {
			scale: 1.15,
			transition: {
				type: 'spring',
				stiffness: 400,
				damping: 15
			}
		},
		tap: { scale: 0.9 }
	}

	return (
		<Box
			position="fixed"
			top="0"
			left="0"
			right="0"
			display="flex"
			justifyContent="center"
			px={4}
			py={6}
			zIndex={1000}
			pointerEvents="none" // Allows clicking through the outer box if needed
		>
			<MotionFlex
				variants={navbarVariants}
				initial="initial"
				animate="animate"
				bg={t.navBg}
				backdropFilter="blur(16px)"
				border="1px solid"
				borderColor={t.borderSubtle}
				borderRadius="full"
				px={isMobile ? 2 : 4}
				py={2}
				gap={isMobile ? 1 : 2}
				boxShadow={t.navShadow}
				alignItems="center"
				pointerEvents="auto" // Re-enable pointer events for the navbar itself
			>
				<Flex gap={isMobile ? 1 : 2} alignItems="center">
					{defaultNavItems.map((item) => {
						const isActive = location.pathname === item.path
						return (
							<Tooltip
								key={item.path || item.anchor}
								label={item.name}
								hasArrow
								bg={t.tooltipBg}
								color={t.tooltipColor}
								borderRadius="md"
								fontSize="xs"
								offset={[0, 10]}>
								<Box position="relative">
									<MotionBox variants={itemVariants} whileHover="hover" whileTap="tap">
										<IconButton
											icon={item.icon}
											aria-label={item.name}
											variant="ghost"
											borderRadius="full"
											onClick={() => navigate(item.path)}
											color={isActive ? t.accent : t.iconDefault}
											_hover={{
												bg: t.hoverBgSubtle,
												color: t.accent
											}}
											_active={{ bg: 'transparent' }}
											fontSize={isMobile ? 'lg' : 'xl'}
											size={isMobile ? 'sm' : 'md'}
											transition="color 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
										/>
									</MotionBox>
									<AnimatePresence>
										{isActive && (
											<MotionBox
												layoutId="active-indicator"
												position="absolute"
												bottom="-4px"
												left="25%"
												right="25%"
												h="2.5px"
												bg={t.accent}
												borderRadius="full"
												initial={{ opacity: 0, scale: 0 }}
												animate={{ opacity: 1, scale: 1 }}
												exit={{ opacity: 0, scale: 0 }}
												transition={{
													type: 'spring',
													bounce: 0.3,
													duration: 0.6
												}}
												boxShadow={`0 0 10px ${t.accentGlow}`}
											/>
										)}
									</AnimatePresence>
								</Box>
							</Tooltip>
						)
					})}
				</Flex>

				<Box w="1px" h="24px" bg={t.divider} mx={isMobile ? 1 : 2} />

				<Box ref={settingsRef} position="relative" flex="0 0 auto">
					<Tooltip label="Settings" hasArrow borderRadius="md" fontSize="xs">
						<IconButton
							icon={<FiSettings />}
							aria-label="Settings"
							variant="ghost"
							borderRadius="full"
							size={isMobile ? 'sm' : 'md'}
							fontSize={isMobile ? 'lg' : 'xl'}
							color={t.iconDefault}
							bg={settingsOpen ? t.hoverBgSubtle : 'transparent'}
							onClick={() => setSettingsOpen((open) => !open)}
							_hover={{
								bg: t.hoverBgSubtle,
								transform: 'rotate(45deg)',
								color: t.accent
							}}
							_active={{ transform: 'scale(0.95)' }}
							transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
						/>
					</Tooltip>
					<AnimatePresence>
						{settingsOpen && (
							<MotionBox
								position="absolute"
								top="calc(100% + 10px)"
								right="0"
								bg={t.bg}
								border="1px solid"
								borderColor={t.borderSubtle}
								backdropFilter="blur(20px)"
								boxShadow={t.navShadow}
								w="248px"
								p={3}
								borderRadius="2xl"
								zIndex={1001}
								initial={{ opacity: 0, y: -4, scale: 0.98 }}
								animate={{ opacity: 1, y: 0, scale: 1 }}
								exit={{ opacity: 0, y: -4, scale: 0.98 }}
								transition={{ duration: 0.14, ease: [0.22, 1, 0.36, 1] }}>
								<Flex align="center" justify="space-between" mb={3}>
									<Text color={t.textPrimary} fontSize="sm" fontWeight="semibold" lineHeight="1">
										Display
									</Text>
									<Text color={t.groupLabel} fontSize="10px" fontWeight="medium" lineHeight="1" textTransform="uppercase">
										{colorModePreference === 'system' ? 'Auto' : colorMode}
									</Text>
								</Flex>

								<Box>
									<Text color={t.groupLabel} fontSize="10px" fontWeight="semibold" lineHeight="1" mb={2} textTransform="uppercase">
										Theme
									</Text>
									<Flex align="center" gap={1} bg={t.hoverBgSubtle} borderRadius="full" p="3px">
										{[
											{ id: 'system', icon: <FiMonitor />, label: 'Auto' },
											{ id: 'light', icon: <FiSun />, label: 'Light' },
											{ id: 'dark', icon: <FiMoon />, label: 'Dark' }
										].map((item) => {
											const isSelected = colorModePreference === item.id

											return (
												<Tooltip key={item.id} label={item.label} hasArrow borderRadius="md" fontSize="xs">
													<IconButton
														icon={item.icon}
														aria-label={`${item.label} mode`}
														aria-pressed={isSelected}
														size="sm"
														variant="ghost"
														borderRadius="full"
														flex="1"
														minW="0"
														color={isSelected ? t.accent : t.iconDefault}
														bg={isSelected ? t.surfaceAlpha : 'transparent'}
														boxShadow={isSelected ? 'sm' : 'none'}
														onClick={() => handleColorPreference(item.id)}
														_hover={{ bg: isSelected ? t.surfaceAlpha : t.hoverBgSubtle, color: t.accent }}
														_active={{ transform: 'scale(0.94)' }}
													/>
												</Tooltip>
											)
										})}
									</Flex>
								</Box>

								<Box h="1px" bg={t.borderSubtle} my={3} />

								<Box>
									<Text color={t.groupLabel} fontSize="10px" fontWeight="semibold" lineHeight="1" mb={2} textTransform="uppercase">
										Accent
									</Text>
									<Flex align="center" justify="space-between" bg={t.hoverBgSubtle} borderRadius="full" p="3px">
										{accentPalettes.map((palette) => {
											const isSelected = accentId === palette.id

											return (
												<Tooltip key={palette.id} label={palette.name} hasArrow borderRadius="md" fontSize="xs">
													<IconButton
														aria-label={`${palette.name} accent`}
														aria-pressed={isSelected}
														size="sm"
														variant="ghost"
														borderRadius="full"
														color={isSelected ? t.accent : t.iconDefault}
														bg={isSelected ? t.surfaceAlpha : 'transparent'}
														onClick={() => setAccentId(palette.id)}
														_hover={{ bg: isSelected ? t.surfaceAlpha : t.hoverBgSubtle }}
														_active={{ transform: 'scale(0.94)' }}
														icon={
															<Box
																position="relative"
																w="16px"
																h="16px"
																borderRadius="full"
																bg={palette.accent}
																boxShadow={isSelected ? `0 0 12px ${palette.accentGlow}` : 'none'}>
																{isSelected && (
																	<Box
																		as={FiCheck}
																		position="absolute"
																		top="50%"
																		left="50%"
																		transform="translate(-50%, -50%)"
																		color="white"
																		fontSize="10px"
																		strokeWidth="3"
																	/>
																)}
															</Box>
														}
													/>
												</Tooltip>
											)
										})}
									</Flex>
								</Box>

								<Box h="1px" bg={t.borderSubtle} my={3} />

								<Flex align="center" justify="space-between" gap={3}>
									<Flex align="center" gap={2} minW="0">
										<Flex
											align="center"
											justify="center"
											w="28px"
											h="28px"
											borderRadius="full"
											color={t.iconDefault}
											bg={t.hoverBgSubtle}
											flex="0 0 auto">
											<Box as={FiBriefcase} fontSize="14px" />
										</Flex>
										<Box minW="0" textAlign="left">
											<Text color={t.textPrimary} fontSize="xs" fontWeight="medium" lineHeight="1.1">
												Recruiter Mode
											</Text>
											<Text color={t.textMuted} fontSize="10px" lineHeight="1.2" mt={1}>
												Focused hiring view
											</Text>
										</Box>
									</Flex>

									<Flex
										as="button"
										type="button"
										align="center"
										position="relative"
										w="42px"
										h="24px"
										p="3px"
										borderRadius="full"
										bg={t.hoverBgSubtle}
										aria-label="Turn on recruiter mode"
										aria-pressed={false}
										onClick={() => {
											setSettingsOpen(false)
											navigate('/recruiter')
										}}
										_hover={{ bg: t.hoverBgSubtle }}
										_active={{ transform: 'scale(0.96)' }}
										transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)">
										<MotionBox
											position="absolute"
											top="3px"
											left="3px"
											w="18px"
											h="18px"
											borderRadius="full"
											bg={t.iconDefault}
											animate={{ x: 0 }}
											transition={{ type: 'spring', stiffness: 520, damping: 32, mass: 0.7 }}
										/>
									</Flex>
								</Flex>

								<Box h="1px" bg={t.borderSubtle} my={3} />

								<Flex align="center" justify="space-between" gap={3}>
									<Flex align="center" gap={2} minW="0">
										<Flex
											align="center"
											justify="center"
											w="28px"
											h="28px"
											borderRadius="full"
											color={showBackground ? t.accent : t.iconDefault}
											bg={showBackground ? t.accentSoft : t.hoverBgSubtle}
											flex="0 0 auto">
											<Box as={FiImage} fontSize="14px" />
										</Flex>
										<Box minW="0">
											<Text color={t.textPrimary} fontSize="xs" fontWeight="medium" lineHeight="1.1">
												Background
											</Text>
											<Text color={t.textMuted} fontSize="10px" lineHeight="1.2" mt={1}>
												{showBackground ? 'Motion on' : 'Motion off'}
											</Text>
										</Box>
									</Flex>

									<Flex
										as="button"
										type="button"
										align="center"
										position="relative"
										w="42px"
										h="24px"
										p="3px"
										borderRadius="full"
										bg={showBackground ? t.accentSoft : t.hoverBgSubtle}
										aria-label={showBackground ? 'Hide background animation' : 'Show background animation'}
										aria-pressed={showBackground}
										onClick={() => setShowBackground(!showBackground)}
										_hover={{ bg: showBackground ? t.accentSoft : t.hoverBgSubtle }}
										_active={{ transform: 'scale(0.96)' }}
										transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)">
										<MotionBox
											position="absolute"
											top="3px"
											left="3px"
											w="18px"
											h="18px"
											borderRadius="full"
											bg={showBackground ? t.accent : t.iconDefault}
											boxShadow={showBackground ? `0 0 10px ${t.accentGlow}` : 'none'}
											animate={{ x: showBackground ? 18 : 0 }}
											transition={{ type: 'spring', stiffness: 520, damping: 32, mass: 0.7 }}
										/>
									</Flex>
								</Flex>
							</MotionBox>
						)}
					</AnimatePresence>
				</Box>
			</MotionFlex>
		</Box>
	)
}

export default Navbar
